import { LinearProgress } from "@mui/material"
import { Dispatch, SetStateAction, useEffect, useState } from "react"

import { ModifiedIncident } from ".."
import { RichTextEditor } from "src/components/common/rich-text-editor"
import { create } from "src/helpers/bem"
import { fetchPurchasedServices } from "src/queriesXRM/purchaseorder"
import { useAccount } from "src/states/account"
import { useBooking } from "src/states/bookingDetail"
import { useBookings } from "src/states/bookings"
import { Dialogs, useDialog } from "src/states/dialogs"
import { useLocale } from "src/states/locale"
import { TranslationMessages } from "src/translations"
import {
  xRMWebshopOrderedCategories,
  xRMWebshopOrdersByDebitor,
} from "src/types/xRM"

import BookingSelect from "./BookingSelect"

import styles from "./CancellationFields.module.scss"
import ProductCategory from "./ProductCategory"

const bem = create(styles, "CancellationFields")

export type CategorySelection = {
  categoryId: string
  selectedItemIds: string[]
}

type CancellationFieldsProps = {
  messages: TranslationMessages
  incident: ModifiedIncident
  setIncident: Dispatch<SetStateAction<ModifiedIncident>>
}

export const CancellationFields = ({
  messages,
  incident,
  setIncident,
}: CancellationFieldsProps) => {
  const locale = useLocale()
  const { booking } = useBooking()
  const { bookings } = useBookings()
  const [isLoading, setIsLoading] = useState(false)
  const {
    contact: { debitorId = "e98a64b5-265b-f011-bec1-7c1e522787e2" } = {},
  } = useAccount()
  const [selectedBookingId, setSelectedBookingId] = useState<string>(
    booking?.id || bookings[0]?.id || "",
  )
  const [webshopCategories, setWebshopCategories] = useState<
    xRMWebshopOrderedCategories[]
  >([])
  const { disabled: isReadOnly } = useDialog(
    "incidentDialog",
  ) as Dialogs["incidentDialog"]

  const { description } = incident

  useEffect(() => {
    const getOrders = async () => {
      try {
        setIsLoading(true)
        const orders: xRMWebshopOrdersByDebitor[] =
          await fetchPurchasedServices(selectedBookingId)
        const debitorOrders = orders.find(
          (o: xRMWebshopOrdersByDebitor) => o.debitorId === debitorId,
        )
        setWebshopCategories(debitorOrders?.categories ?? [])
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }

    getOrders()
  }, [selectedBookingId, locale, debitorId])

  return (
    <div className={bem()}>
      <BookingSelect
        messages={messages}
        selectedBookingId={selectedBookingId}
        setIncident={setIncident}
        setSelectedBookingId={setSelectedBookingId}
      />

      <div className={bem("description")}>
        <h5 className={bem("description__label")}>
          {
            messages.pages.account.overview.profile.incidents.createDialog
              .cancellationFields.description.label
          }
        </h5>
        <RichTextEditor
          readOnly={isReadOnly}
          value={description ?? ""}
          placeholder={
            messages.pages.account.overview.profile.incidents.createDialog
              .cancellationFields.description.placeholder
          }
          onChange={(value) =>
            setIncident((prev) => ({ ...prev, description: value }))
          }
        />
      </div>

      <div className={bem("products")}>
        <div className={bem("products__title")}>
          {
            messages.pages.account.overview.profile.incidents.createDialog
              .cancellationFields.products.title
          }
        </div>
        <div className={bem("products__subtitle")}>
          {
            messages.pages.account.overview.profile.incidents.createDialog
              .cancellationFields.products.subtitle
          }
        </div>
        {isLoading ? (
          <div className={bem("products__loading")}>
            <LinearProgress color="primary" />
          </div>
        ) : (
          <div className={bem("products__categories")}>
            {webshopCategories.map((category) => (
              <ProductCategory
                key={category.categoryId}
                category={category}
                incident={incident}
                selectedBookingId={selectedBookingId}
                setIncident={setIncident}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CancellationFields
